import { reactive, UnwrapRef, ref, Ref, computed, ComputedRef } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { formatSize, exportCSVInBrowser, exportCSVInPC, phoneCompose } from '@/renderer/scripts/utils/tools/index';
import { IS_BROWSER } from '@/common/constant/platform';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';
import { getDownloadPath } from '@/renderer/scripts/utils/download/downloadHelper';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

let fs: typeof import('fs');
let path: typeof import('path');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
}

interface ItemSpaceInfo {
  id: number;
  capacity: string;
  size: string;
}
interface DefaultSort {
  sortBy: string;
  descending: boolean;
}
type TableData = {
  keyword: string;
  list: Array<object>;
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
};

type Handler = {
  tabValue: Ref<string>;
  columns: Ref<any[]>;
  tableData: UnwrapRef<TableData>;
  defaultSort: ComputedRef<DefaultSort>;
  isExporting: ComputedRef<boolean>;
  showEditSpace: Ref<boolean>;
  itemSpaceInfo: ItemSpaceInfo;
  sort: Sort;
  methods: {
    pullList: () => Promise<void>;
    searchTable: () => void;
    clearSearchTable: () => void;
    onPageSizeChange: (pageSize: number) => void;
    onCurrentChange: (current: number) => void;
    exportAll: () => Promise<void>;
    setTeamSapce: (teamId: number, capacity: string | null) => Promise<void>;
    setPersonalSapce: (userId: number, capacity: string | null) => Promise<void>;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
  };
};
export default function spaceHandler(listType: 'size' | 'traffic'): Handler {
  const tabValue = ref('teamSpace');
  const showEditSpace = ref(false);
  const editingRowIndex = ref(-1);
  const currentUserRole = computed(() => store.getters.userBaseInfo.role);
  const columnMap = {
    size: {
      teamColumns: [
        {
          colKey: 'name',
          title: i18n_t('团队名称'),
        },
        {
          colKey: 'totalSize',
          title: 'title-totalSize',
          titleDesc: i18n_t('分配容量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'usedSize',
          title: 'title-usedSize',
          titleDesc: i18n_t('已使用容量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'restSize',
          title: 'title-restSize',
          titleDesc: i18n_t('剩余容量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'userCount',
          title: i18n_t('团队成员'),
          align: 'right',
          width: i18n.locale === 'zh-CN' ? '120' : '150',
        },
      ],
      personalColumns: [
        {
          colKey: 'name',
          title: i18n_t('用户昵称'),
        },
        {
          colKey: 'totalSize',
          title: 'title-totalSize',
          titleDesc: i18n_t('分配容量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'usedSize',
          title: 'title-usedSize',
          titleDesc: i18n_t('已使用容量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'restSize',
          title: 'title-restSize',
          titleDesc: i18n_t('剩余容量'),
          align: 'right',
          sorter: true,
        },
      ],
    },
    traffic: {
      teamColumns: [
        {
          colKey: 'name',
          title: i18n_t('团队名称'),
        },
        {
          colKey: 'usedSize',
          title: 'title-usedSize',
          titleDesc: i18n_t('团队已使用空间'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'trafficSize',
          title: 'title-trafficSize',
          titleDesc: i18n_t('外网流量用量'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'userCount',
          title: i18n_t('团队成员'),
          align: 'right',
        },
      ],
      personalColumns: [
        {
          colKey: 'name',
          title: i18n_t('用户昵称'),
        },
        {
          colKey: 'totalTraffic',
          title: 'title-totalTraffic',
          titleDesc: i18n_t('个人外网流量使用'),
          align: 'right',
          sorter: true,
        },
        {
          colKey: 'spacesTraffic',
          title: 'title-spacesTraffic', // 等同于旧的“外网流量使用”
          titleDesc: i18n_t('个人空间外网流量使用'),
          // 等同于旧的“外网流量使用”
          align: 'right',
          sorter: true,
          width: 260,
        },
        {
          colKey: 'shareTraffic',
          title: 'title-shareTraffic',
          titleDesc: i18n_t('外链流量用量'),
          align: 'right',
          sorter: true,
        },
      ],
    },
  };
  const columns = computed(() =>
    tabValue.value === 'teamSpace' ? columnMap[listType].teamColumns : columnMap[listType].personalColumns,
  );
  const tableData = reactive<TableData>({
    keyword: '',
    list: [],
    loading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const defaultSort = computed(() => {
    const sortBy = listType === 'size' ? 'usedSize' : 'trafficSize';
    return {
      sortBy,
      descending: true,
    };
  });
  const sortFieldMap: Record<string, string> = {
    totalSize: 'capacity',
    usedSize: 'size',
    restSize: 'availableSpace',
    trafficSize: 'internetTraffic',
    spacesTraffic: 'spacesTraffic',
    totalTraffic: 'totalTraffic',
    shareTraffic: 'shareTraffic',
  };
  const isExporting = computed(() => {
    if (listType === 'size') {
      return tabValue.value === 'teamSpace'
        ? store.getters.statsIsExporting.teamSpace
        : store.getters.statsIsExporting.personalSpace;
    }
    return tabValue.value === 'teamSpace'
      ? store.getters.statsIsExporting.teamTraffic
      : store.getters.statsIsExporting.personalTraffic;
  });
  const downloadTarget = ref('');

  // 临时用的企业剩余空间
  const tmpOrgSpace = ref('');
  const itemSpaceInfo = reactive({
    id: -1,
    capacity: '',
    size: '',
  });
  const { innerSort, tableSort, methods: sortMethods } = useSort(`managerConsole-${listType}-${tabValue.value}`);

  const methods = {
    tabChange(
      value: string,
      context: {
        e: Event;
      },
    ) {
      tabValue.value = `${value}`;
      tableData.pagination.current = 1;
      methods.pullList();
    },
    async pullList() {
      sortMethods.changePage(`managerConsole-${listType}-${tabValue.value}`);
      tableData.loading = true;
      if (tabValue.value === 'teamSpace') {
        methods.getTeamUsedInfo();
      } else {
        methods.getPersonalUsedInfo();
      }
    },
    searchTable() {
      tableData.pagination.current = 1;
      methods.pullList();
    },
    clearSearchTable() {
      tableData.keyword = '';
      methods.searchTable();
    },
    // 团队使用情况
    async getTeamUsedInfo() {
      try {
        const res = await smh.userAPI.getTeamUsedInfo({
          page: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          keyword: tableData.keyword,
          orderBy: sortFieldMap[innerSort.value.orderBy || defaultSort.value.sortBy],
          orderByType: innerSort.value.orderByType,
        });
        const { contents, totalNum } = res.data;
        tableData.loading = false;
        tableData.pagination.total = totalNum;
        tableData.list = methods.formatList(contents);
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 个人使用情况
    async getPersonalUsedInfo() {
      try {
        const res = await smh.userAPI.getPersonalUsedInfo({
          page: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          keyword: tableData.keyword,
          orderBy: sortFieldMap[innerSort.value.orderBy || defaultSort.value.sortBy],
          orderByType: innerSort.value.orderByType,
        });
        const { contents, totalNum } = res.data;
        tableData.loading = false;
        tableData.pagination.total = totalNum;
        tableData.list = methods.formatList(contents);
      } catch (e: any) {
        tips.error(e);
      }
    },
    formatItem(item: any) {
      const { capacity, shareLibraryRemainingQuota, allowPersonalSpace } = item;
      const size = item.size || '0';
      const availableSpace = item.availableSpace || '0';
      const traffic = item.internetTraffic ? `${item.internetTraffic}` : '0';
      const totalSize = shareLibraryRemainingQuota ? item.availableSpace : capacity;
      const totalSizeInfo = formatSize(totalSize);
      const usedSizeInfo = formatSize(size);
      const restSizeInfo = formatSize(availableSpace);
      const trafficInfo = formatSize(traffic);
      const spacesTraffic = item.spacesTraffic || '0'; // 个人空间外网流量使用
      const totalTraffic = item.totalTraffic || '0'; // 个人外网流量使用
      const shareTraffic = item.shareTraffic || '0'; // 外链流量用量
      const spacesTrafficInfo = formatSize(spacesTraffic);
      const totalTrafficInfo = formatSize(totalTraffic);
      const shareTrafficInfo = formatSize(shareTraffic);
      // 表格里展示全名时不展示根团队比如根团队是a，当前团队是a/b/c，名称只展示b/c
      const pathStr = item.path && item.path.length > 2 ? `${item.path.slice(1, -1).join('/')}` : '';
      let phoneNumber = null;
      if (tabValue.value === 'personalSpace') {
        phoneNumber = phoneCompose(item.countryCode, item.phoneNumber);
      }
      // 管理员不能修改管理员的配额，超级管理员可以修改所有人的配额
      let editPersonalCapacityDisabled = false;
      if (currentUserRole.value === 'superAdmin') {
        editPersonalCapacityDisabled = false;
      } else if (currentUserRole.value === 'admin') {
        editPersonalCapacityDisabled = checkRoleIsAdmin(item.role, true);
      }
      // 存一个当前共享企业剩余的额度
      if (item.shareLibraryRemainingQuota) {
        tmpOrgSpace.value = item.availableSpace;
      }
      const noPersonalSpace = tabValue.value === 'personalSpace' && !item.allowPersonalSpace;
      return {
        capacity,
        availableSpace,
        size,
        id: item.id,
        pathStr,
        allowPersonalSpace,
        shareLibraryRemainingQuota,
        name: item.nickname || item.name,
        totalSize: noPersonalSpace ? i18n_t('未分配') : `${totalSizeInfo.size} ${totalSizeInfo.unit}`,
        usedSize: noPersonalSpace ? i18n_t('未分配') : `${usedSizeInfo.size} ${usedSizeInfo.unit}`,
        // eslint-disable-next-line no-nested-ternary
        restSize: noPersonalSpace
          ? i18n_t('未分配')
          : restSizeInfo.size < 0
          ? '0 B'
          : `${restSizeInfo.size} ${restSizeInfo.unit}`,
        trafficSize: `${trafficInfo.size}${trafficInfo.unit}`,
        spacesTraffic: noPersonalSpace ? i18n_t('未分配') : `${spacesTrafficInfo.size} ${spacesTrafficInfo.unit}`,
        totalTraffic: `${totalTrafficInfo.size} ${totalTrafficInfo.unit}`,
        shareTraffic: `${shareTrafficInfo.size} ${shareTrafficInfo.unit}`,
        userCount: `${item.userCount || 0}`,
        editPersonalCapacityDisabled: tabValue.value === 'personalSpace' && editPersonalCapacityDisabled,
        phoneNumber,
        showEditIcon: (tabValue.value === 'personalSpace' && allowPersonalSpace) || tabValue.value === 'teamSpace',
      };
    },
    formatList(list: any[]) {
      const resList = list.map(item => methods.formatItem(item));
      return resList;
    },
    onPageSizeChange(pageSize: number) {
      tableData.pagination.pageSize = pageSize;
      methods.pullList();
    },
    onCurrentChange(current: number) {
      tableData.pagination.current = current;
      methods.pullList();
    },

    switchSort(columnName: string) {
      console.log('switchSort', tabValue.value);
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullList();
    },
    // 全量导出
    async exportAll() {
      if (store.getters.isTclApp) {
        // tcl 渠道不能下载
        tips.error(i18n_t('暂不支持下载'));
        return;
      }
      // pc端获得下载路径
      if (!IS_BROWSER) {
        downloadTarget.value = await getDownloadPath();
        if (!downloadTarget.value) return;
      }
      tips.info(i18n_t('后台导出中，请稍后'));
      const tabType = tabValue.value;
      let storeComitKey = '';
      if (listType === 'size') {
        storeComitKey = tabType === 'teamSpace' ? 'teamSpace' : 'personalSpace';
      } else {
        storeComitKey = tabType === 'teamSpace' ? 'teamTraffic' : 'personalTraffic';
      }
      store.commit('setStatsIsExporting', {
        [storeComitKey]: true,
      });
      const allList = await methods.getAllList();
      const ext = listType === 'size' ? i18n_t('容量使用详情') : i18n_t('外网流量使用详情');
      const csvName = `${tabType === 'teamSpace' ? i18n_t('企业空间') : i18n_t('个人空间')}${ext}`;
      store.commit('setStatsIsExporting', {
        [storeComitKey]: false,
      });
      const exportColumns = columns.value.map(item => {
        const titleStr = item.titleDesc || item.title;
        return {
          colKey: item.colKey,
          title: titleStr,
        };
      });
      methods.exportToLocal(exportColumns, allList, csvName);
      tips.success(i18n_t('导出完成'));
    },
    // 轮询所有列表
    async getAllList() {
      let allList = [];
      const tabType = tabValue.value;
      const loopGetList = async (page: number, list: any[]): Promise<any[]> => {
        const fn = tabType === 'teamSpace' ? smh.userAPI.getTeamUsedInfo : smh.userAPI.getPersonalUsedInfo;
        const res = await fn({
          page,
          pageSize: 50,
          keyword: '',
          orderBy: sortFieldMap[innerSort.value.orderBy || defaultSort.value.sortBy],
          orderByType: innerSort.value.orderByType,
        });
        const { contents, pageSize, totalNum } = res.data;
        list.push(...contents);
        if (page * pageSize < totalNum) {
          return loopGetList(page + 1, list);
        }
        return list;
      };
      try {
        let list = await loopGetList(1, []);
        list = list.map(item => {
          const formattedItem = methods.formatItem(item);
          return {
            ...formattedItem,
            name: item.path && item.path.length > 1 ? item.path.slice(1).join('/') : formattedItem.name,
          };
        });
        allList = list;
      } catch (e: any) {
        tips.error(e);
      }
      return allList;
    },
    // 导出到本地
    exportToLocal(columns: any[], list: any[], exportName: string) {
      const ext = '.csv';
      if (IS_BROWSER) {
        exportCSVInBrowser(columns, list, `${exportName}${ext}`);
      } else {
        exportCSVInPC(columns, list, `${exportName}${ext}`, downloadTarget.value);
      }
    },
    // 设置空间容量
    editSpace(row: any, rowIndex: number) {
      if (row.editPersonalCapacityDisabled) return;
      showEditSpace.value = true;
      editingRowIndex.value = rowIndex;
      Object.assign(itemSpaceInfo, {
        id: row.id,
        capacity: row.capacity,
        size: row.size,
        availableSpace: row.availableSpace,
        shareLibraryRemainingQuota: row.shareLibraryRemainingQuota,
      });
    },
    async setTeamSapce(teamId: number, capacity: string | null, openSpace?: boolean) {
      try {
        await smh.userAPI.setTeamSpace({
          teamId,
          capacity: capacity === null ? '' : capacity,
          ...(openSpace && { openSpace }),
        });
        tips.success(i18n_t('保存成功'));
        showEditSpace.value = false;
        methods.pullList();
        // 设置成功后更新一次企业空间总数、剩余数
        store.dispatch('GetOrgSpace');
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    async setPersonalSapce(userId: number, capacity: string | null) {
      try {
        await smh.userAPI.setCapacity({
          userId,
          capacity: capacity === null ? '' : capacity,
        });
        tips.success(i18n_t('保存成功'));
        showEditSpace.value = false;
        methods.pullList();
        // 设置成功后更新一次企业空间总数、剩余数
        store.dispatch('GetOrgSpace');
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
  };
  return {
    tabValue,
    columns,
    tableData,
    defaultSort,
    isExporting,
    showEditSpace,
    itemSpaceInfo,
    sort: tableSort,
    methods,
  };
}
